import Loader from '@/components/Loader';
import { ApplicationState } from '@/store';
import { getHistoryLearningMapRequest } from '@/store/learningMap/action';
import { LearningMap } from '@/store/learningMap/types';
import { LocaleData } from '@/translations/LocaleData';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Hint, HorizontalGridLines, LineMarkSeries, LineSeries, XAxis, XYPlot, YAxis } from 'react-vis';
import _ from 'underscore';
import './style.scss';

interface Point {
    x: number;
    y: number;
    consumedData: number;
}

type VisData = {
    data: Point[];
    goalsData: Point[];
};

const CHART_HEIGHT = 300;
const verticalChartSize = 250;

const defaultGraphSize = { width: 400 };

const makeVisData = (): VisData => ({
    data: [{ x: 0, y: 0, consumedData: 0 }],
    goalsData: [],
});

const defaultVisData: VisData = makeVisData();

const getLabelForY = (value: number): string => {
    const label: { [key: number]: string } = { 0: '0', 30: '30min', 60: '1h', 120: '2h', 240: '4h' };
    return label[value] || ``;
};

const getTooltipValue = (value: Point) => {
    const ONE_HOUR_IN_MINUTES: number = 60;
    const h: number = Math.floor(value.consumedData / ONE_HOUR_IN_MINUTES);
    const m: number = value.consumedData % ONE_HOUR_IN_MINUTES;

    let result: string = '';

    if (h > 0) {
        result = `${h}h`;
    }

    if (m > 0) {
        result += `${m}min`;
    }

    return result || '0';
};

const Legend = ({ strings }: { strings: LocaleData }) => (
    <div className="legend">
        <div className="legend-item">
            <div className="legend-dash"></div>
            <span>{strings.STUDY_TIME}</span>
        </div>
        <div className="legend-item">
            <div className="legend-circle"></div>
            <span>{strings.GOAL_OF_THE_WEEK}</span>
        </div>
    </div>
);

const LearningMapHistoryChart: React.FC<any> = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [visData, setVisData] = useState<VisData>(defaultVisData);
    const [chartWidth, setChartWidth] = useState<number>(CHART_HEIGHT);
    const [selectedPoint, setSelectedPoint] = useState<any>(null);
    const [data, setData] = useState<Point[]>();
    const [goalsData, setGoalsData] = useState<Point[]>();
    const containerRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    const { strings, primaryColor, historyLearningMapList, currentLearningMap } = useSelector((state: ApplicationState) => {
        const [primaryColor, ..._] = state.appOrganization.data.colors;
        return {
            strings: state.locale.data,
            historyLearningMapList: state.learningMap.historyLearningMapList,
            currentLearningMap: state.learningMap.data.currentLearningMap,
            primaryColor,
        };
    });

    const getLabelForX = (value: number): string => {
        const label: string[] = ['', strings.WEEK_ONE, strings.WEEK_TWO, strings.WEEK_THREE, strings.WEEK_FOUR, ''];
        return label[value];
    };

    useEffect(() => {
        setLoading(true);
        dispatch(getHistoryLearningMapRequest());
    }, [currentLearningMap]);

    useEffect(() => {
        if (historyLearningMapList.length === 0) {
            return;
        }

        const transformed = _.reduce(
            [...historyLearningMapList].reverse(),
            (acc, lm: LearningMap, index: number) => {
                acc.data.push({ x: index + 1, y: Math.min(verticalChartSize, lm.consumedMinutes), consumedData: lm.consumedMinutes });
                acc.goalsData.push({ x: index + 1, y: lm.minutes, consumedData: 0 });
                return acc;
            },
            makeVisData(),
        );
        setVisData(transformed);
        setLoading(false);
    }, [historyLearningMapList]);

    useEffect(() => {
        const { width } = (containerRef.current && containerRef.current.getBoundingClientRect()) || defaultGraphSize;
        setChartWidth(width - 40);
    }, [containerRef.current]);

    useEffect(() => {
        setData(visData.data);
        setGoalsData(visData.goalsData);
    }, [visData]);

    return _.isEmpty(visData) ? null : (
        <div className="learning-map-history-chart" ref={containerRef}>
            <h4 className="section-title">{strings.HISTORY_OF_THE_LAST_4_WEEKS}</h4>
            {loading ? (
                <Loader />
            ) : (
                <>
                    <XYPlot
                        color={primaryColor}
                        height={CHART_HEIGHT}
                        margin={{ left: 45 }}
                        stroke={primaryColor}
                        width={chartWidth}
                        xDomain={[0, 5]}
                        yDomain={[0, 240]}
                    >
                        <HorizontalGridLines style={{ strokeDasharray: 2, strokeWidth: 2 }} tickValues={[0, 30, 60, 120, 240]} />
                        <XAxis style={{ strokeWidth: 2 }} tickFormat={getLabelForX} tickValues={[0, 1, 2, 3, 4, 5]} />
                        <YAxis style={{ strokeWidth: 2 }} tickFormat={getLabelForY} tickValues={[0, 30, 60, 120, 240]} />
                        <LineSeries
                            data={data}
                            onNearestXY={(value) => setSelectedPoint(value)}
                            onValueMouseOut={() => setSelectedPoint(null)}
                            style={{ strokeWidth: 3 }}
                        />
                        <LineMarkSeries data={goalsData} style={{ strokeWidth: 0 }} />
                        {selectedPoint && (
                            <Hint align={{ vertical: 'auto' }} style={{ position: 'relative' }} value={selectedPoint}>
                                <div className="history-hint">
                                    <p>{getTooltipValue(selectedPoint)}</p>
                                </div>
                            </Hint>
                        )}
                    </XYPlot>
                    <Legend strings={strings} />
                </>
            )}
        </div>
    );
};

export default LearningMapHistoryChart;
